import React, { useId } from 'react';
import { FlowGraphData, TraceStep, fmtMs, num, statusStyle } from './flowTypes';
import { Frame, Layout, NodeBox, SubBox, isParallel } from './flowLayout';

/**
 * The Search-flow graph as one SVG: lane bands, edges (smooth paths with
 * arrowheads, fallback edges dashed red), nodes coloured by kind, the
 * "understanding" parallel node as a group box holding its sub-steps, and —
 * while a scenario or a real run plays — flowing dashes on the path, a
 * travelling dot and a pulse on the node being visited. Pure render: every
 * moving part comes in through `frame` (flowLayout.frameAt).
 */

export type Selection = { kind: 'node'; id: string; sub?: string } | { kind: 'edge'; id: string };
export type FlowMode = 'overview' | 'scenario' | 'trace';

export const KIND_STYLE: Record<string, { fill: string; stroke: string; label: string; dash?: string }> = {
  step:     { fill: '#FFFFFF', stroke: '#C9B392', label: 'step' },
  decision: { fill: '#FFF7EA', stroke: '#C98A2B', label: 'decision' },
  parallel: { fill: '#F3F7FC', stroke: '#5085C4', label: 'parallel', dash: '6 4' },
  store:    { fill: '#F0F8F3', stroke: '#5E9E74', label: 'store' },
  external: { fill: '#F7F3FC', stroke: '#8B6BB8', label: 'external', dash: '4 3' },
};
export const kindStyle = (k: string) => KIND_STYLE[k] || KIND_STYLE.step;

const ACCENT = '#2C5695';
const DOT = '#AF8E5E';
const EDGE_GREY = '#A3A3A3';
const EDGE_FALLBACK = '#D0643A';
const EDGE_LOOP = '#8B6BB8';

/** Greedy word wrap by character budget; the last line gets an ellipsis. */
export function wrap(text: string | undefined, maxChars: number, maxLines: number): string[] {
  const words = String(text || '').replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  const lines: string[] = [];
  let cur = '';
  for (let i = 0; i < words.length; i++) {
    const w = words[i];
    const next = cur ? `${cur} ${w}` : w;
    if (next.length <= maxChars) { cur = next; continue; }
    if (cur) lines.push(cur);
    cur = w.length > maxChars ? `${w.slice(0, maxChars - 1)}…` : w;
    if (lines.length === maxLines) { cur = ''; break; }
  }
  if (cur && lines.length < maxLines) lines.push(cur);
  const used = lines.join(' ').split(' ').filter(Boolean).length;
  if (lines.length && used < words.length) {
    const last = lines[lines.length - 1];
    lines[lines.length - 1] = `${last.length > maxChars - 1 ? last.slice(0, maxChars - 1) : last}…`;
  }
  return lines.slice(0, maxLines);
}

export const FLOW_CSS = `
@keyframes sfDash { to { stroke-dashoffset: -28; } }
.sf-flow { stroke-dasharray: 8 6; animation: sfDash .9s linear infinite; }
@keyframes sfPulse { 0% { opacity: .8; transform: scale(1); } 100% { opacity: 0; transform: scale(1.16); } }
.sf-pulse { transform-box: fill-box; transform-origin: center; animation: sfPulse 1.1s ease-out infinite; }
@keyframes sfBlink { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
.sf-run { animation: sfBlink .9s ease-in-out infinite; }
.sf-node { cursor: pointer; }
.sf-node:hover .sf-shape { stroke-width: 2.4px; }
.sf-node:focus { outline: none; }
.sf-node:focus-visible .sf-shape { stroke: #AF8E5E; stroke-width: 2.6px; }
.sf-edge-hit { cursor: pointer; }
.sf-halo { paint-order: stroke; stroke: #FFFFFF; stroke-width: 3px; stroke-linejoin: round; }
@media (prefers-reduced-motion: reduce) { .sf-flow, .sf-pulse, .sf-run { animation: none; } }
`;

interface Props {
  graph: FlowGraphData;
  layout: Layout;
  mode: FlowMode;
  pathNodes: Set<string>;
  pathEdges: Set<string>;
  frame: Frame;
  /** trace only: status + ms of each node (its last visit) */
  nodeStatus: Map<string, { status: string; ms: number | null; t: number | null }>;
  /** trace only: `${node}/${sub}` -> that sub-step's step */
  subSteps: Map<string, TraceStep>;
  selected: Selection | null;
  onSelect: (s: Selection) => void;
  scale: number;
}

const FlowGraph: React.FC<Props> = ({
  graph, layout, mode, pathNodes, pathEdges, frame, nodeStatus, subSteps, selected, onSelect, scale,
}) => {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const mk = (n: string) => `sf${uid}-${n}`;
  const pathMode = mode !== 'overview';

  const edgeList = Array.from(layout.edges.values());
  // Path edges draw last so they sit on top of the dimmed ones.
  edgeList.sort((a, b) => Number(pathEdges.has(a.id)) - Number(pathEdges.has(b.id)));

  const renderEdge = (id: string) => {
    const g = layout.edges.get(id)!;
    const e = g.edge;
    const fallback = e.kind === 'fallback';
    const loop = e.kind === 'loop';
    const onPath = pathEdges.has(id);
    const phase = frame.edge.get(id);
    const sel = selected?.kind === 'edge' && selected.id === id;
    let color = fallback ? EDGE_FALLBACK : loop ? EDGE_LOOP : EDGE_GREY;
    let width = 1.4;
    let opacity = 1;
    let cls = '';
    let dash: string | undefined = fallback ? '5 4' : loop ? '2 3' : undefined;
    let marker = fallback ? 'red' : loop ? 'purple' : 'grey';
    if (pathMode) {
      if (onPath) {
        color = fallback ? '#D0443A' : ACCENT;
        marker = fallback ? 'red' : 'navy';
        width = phase === 'done' || phase === 'active' ? 2.6 : 1.8;
        opacity = phase === 'pending' ? 0.5 : 1;
        cls = 'sf-flow';
        dash = undefined;
      } else {
        opacity = 0.16;
      }
    }
    if (sel) { width += 1; opacity = 1; }
    const title = [e.label, e.condition].filter(Boolean).join(' — ') || `${e.from} → ${e.to}`;
    return (
      <g key={id} opacity={opacity}>
        <path d={g.d} fill="none" stroke={sel ? '#AF8E5E' : color} strokeWidth={width}
          strokeDasharray={dash} className={cls} markerEnd={`url(#${mk(marker)})`} />
        <path d={g.d} fill="none" stroke="transparent" strokeWidth={12} className="sf-edge-hit"
          onClick={ev => { ev.stopPropagation(); onSelect({ kind: 'edge', id }); }}>
          <title>{title}</title>
        </path>
      </g>
    );
  };

  const renderEdgeLabel = (id: string) => {
    const g = layout.edges.get(id)!;
    const text = g.edge.label;
    if (!text) return null;
    const onPath = pathEdges.has(id);
    if (pathMode && !onPath) return null;
    const short = text.length > 26 ? `${text.slice(0, 25)}…` : text;
    const fallback = g.edge.kind === 'fallback';
    return (
      <text key={`l-${id}`} x={g.label.x} y={g.label.y} textAnchor={g.labelAnchor} fontSize={9.5}
        className="sf-halo" fill={fallback ? '#B4462A' : onPath && pathMode ? ACCENT : '#525252'}
        style={{ pointerEvents: 'none' }} fontStyle={fallback ? 'italic' : undefined}>
        {short}
      </text>
    );
  };

  // ------------------------------------------------------------------ nodes
  const nodeLook = (id: string, kind: string) => {
    const base = kindStyle(kind);
    let fill = base.fill; let stroke = base.stroke; let strokeWidth = 1.4; let opacity = 1;
    const phase = frame.node.get(id);
    const st = nodeStatus.get(id);
    if (pathMode) {
      if (!pathNodes.has(id)) opacity = 0.26;
      else if (phase === 'active') { stroke = ACCENT; strokeWidth = 2.6; }
      else if (phase === 'done') {
        if (mode === 'trace' && st) {
          const s = statusStyle(st.status);
          fill = s.fill; stroke = s.stroke; strokeWidth = 2;
        } else { fill = '#EEF3FA'; stroke = ACCENT; strokeWidth = 2; }
      }
    }
    return { fill, stroke, strokeWidth, opacity, dash: base.dash, phase, st };
  };

  const shape = (b: NodeBox, kind: string, look: ReturnType<typeof nodeLook>, extraCls = '') => {
    const common = { fill: look.fill, stroke: look.stroke, strokeWidth: look.strokeWidth,
                     strokeDasharray: look.dash, className: `sf-shape ${extraCls}` };
    if (kind === 'decision') {
      const n = 14;
      const pts = [[b.x + n, b.y], [b.x + b.w - n, b.y], [b.x + b.w, b.y + b.h / 2], [b.x + b.w - n, b.y + b.h],
                   [b.x + n, b.y + b.h], [b.x, b.y + b.h / 2]].map(p => p.join(',')).join(' ');
      return <polygon points={pts} {...common} />;
    }
    if (kind === 'store') {
      return (
        <g>
          <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={8} {...common} />
          <ellipse cx={b.x + b.w / 2} cy={b.y + 1} rx={b.w / 2 - 6} ry={5} fill={look.fill}
            stroke={look.stroke} strokeWidth={1.1} />
        </g>
      );
    }
    return <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={kind === 'parallel' ? 12 : 10} {...common} />;
  };

  const msBadge = (x: number, y: number, status: string, ms: number | null, running = false) => {
    const s = statusStyle(status);
    const text = running ? `${fmtMs(ms)}…` : status === 'skipped' ? 'skipped' : fmtMs(ms);
    const w = Math.max(40, text.length * 5.6 + 12);
    return (
      <g style={{ pointerEvents: 'none' }}>
        <rect x={x - w} y={y} width={w} height={16} rx={8} fill={running ? ACCENT : s.stroke} />
        <text x={x - w / 2} y={y + 11.5} textAnchor="middle" fontSize={9.5} fontWeight={700} fill="#FFFFFF">{text}</text>
      </g>
    );
  };

  const renderNode = (b: NodeBox) => {
    const n = b.node;
    const look = nodeLook(n.id, String(n.kind));
    const isSel = selected?.kind === 'node' && selected.id === n.id && !selected.sub;
    const active = look.phase === 'active';
    const decision = n.kind === 'decision';
    const inset = decision ? 18 : 10;
    const labelChars = Math.floor((b.w - 2 * inset) / 6.6);
    const sumChars = Math.floor((b.w - 2 * inset) / 5.3);
    const top = n.kind === 'store' ? 6 : 0;
    const summary = wrap(n.summary, sumChars, 2);
    const nSub = n.substeps?.length || 0;
    const running = mode === 'trace' && active;
    const runMs = running && frame.activeSeg ? Math.max(0, frame.realNow - frame.activeSeg.realStart) : null;
    return (
      <g key={n.id} className="sf-node" opacity={look.opacity} role="button" tabIndex={0}
        aria-label={`${n.label}: ${n.summary || ''}`}
        onClick={ev => { ev.stopPropagation(); onSelect({ kind: 'node', id: n.id }); }}
        onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onSelect({ kind: 'node', id: n.id }); } }}>
        <title>{`${n.label}${n.code ? ` — ${n.code}` : ''}\n${n.summary || ''}`}</title>
        {isSel && <rect x={b.x - 5} y={b.y - 5} width={b.w + 10} height={b.h + 10} rx={14} fill="none"
          stroke="#AF8E5E" strokeWidth={2.2} />}
        {active && <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={10} fill="none" stroke={ACCENT}
          strokeWidth={3} className="sf-pulse" />}
        {shape(b, String(n.kind), look)}
        <text x={b.x + b.w / 2} y={b.y + 19 + top} textAnchor="middle" fontSize={11.5} fontWeight={700}
          fill="#141414" style={{ pointerEvents: 'none' }}>
          {wrap(n.label, labelChars, 1)[0]}
        </text>
        {summary.map((s, i) => (
          <text key={i} x={b.x + b.w / 2} y={b.y + 34 + top + i * 12.5} textAnchor="middle" fontSize={9.8}
            fill="#525252" style={{ pointerEvents: 'none' }}>{s}</text>
        ))}
        {nSub > 0 && (
          <g style={{ pointerEvents: 'none' }}>
            <rect x={b.x + b.w / 2 - 30} y={b.y + b.h - 7} width={60} height={14} rx={7} fill="#FFFFFF"
              stroke={look.stroke} strokeWidth={1} />
            <text x={b.x + b.w / 2} y={b.y + b.h + 3} textAnchor="middle" fontSize={8.5} fill="#80653F">
              {nSub} sub-step{nSub === 1 ? '' : 's'}
            </text>
          </g>
        )}
        {running && (
          <rect x={b.x + 8} y={b.y + b.h - 5} height={3} rx={1.5} fill={ACCENT}
            width={Math.max(2, (b.w - 16) * frame.nodeFrac)} />
        )}
        {mode === 'trace' && running && msBadge(b.x + b.w + 4, b.y - 10, 'ran', runMs, true)}
        {mode === 'trace' && look.phase === 'done' && look.st && msBadge(b.x + b.w + 4, b.y - 10, look.st.status, look.st.ms)}
      </g>
    );
  };

  const subLook = (nodeId: string, s: SubBox) => {
    const phase = frame.node.get(nodeId);
    const step = subSteps.get(`${nodeId}/${s.subId}`);
    let fill = '#FFFFFF'; let stroke = '#A9C0E0'; let width = 1.2; let cls = '';
    let state: 'idle' | 'running' | 'done' | 'pending' = 'idle';
    let progress = 0;
    if (pathMode && pathNodes.has(nodeId)) {
      if (mode === 'scenario') {
        if (phase === 'active') { fill = '#E3ECF8'; stroke = ACCENT; width = 1.8; cls = 'sf-run'; state = 'running'; }
        else if (phase === 'done') { fill = '#EEF3FA'; stroke = ACCENT; state = 'done'; }
      } else if (step) {
        const t0 = num(step.t_start_ms) ?? 0; const ms = num(step.ms) ?? 0;
        if (phase === 'done' || (phase === 'active' && frame.realNow >= t0 + ms)) state = 'done';
        else if (phase === 'active' && frame.realNow >= t0) {
          state = 'running'; progress = ms > 0 ? (frame.realNow - t0) / ms : 1;
        } else state = 'pending';
        if (state === 'done') { const st = statusStyle(step.status); fill = st.fill; stroke = st.stroke; width = 1.8; }
        if (state === 'running') { fill = '#E3ECF8'; stroke = ACCENT; width = 1.8; cls = 'sf-run'; }
      }
    }
    return { fill, stroke, width, cls, state, progress, step };
  };

  const renderGroup = (b: NodeBox) => {
    const n = b.node;
    const look = nodeLook(n.id, 'parallel');
    const isSel = selected?.kind === 'node' && selected.id === n.id && !selected.sub;
    const active = look.phase === 'active';
    const running = mode === 'trace' && active;
    const runMs = running && frame.activeSeg ? Math.max(0, frame.realNow - frame.activeSeg.realStart) : null;
    return (
      <g key={n.id} opacity={look.opacity}>
        {isSel && <rect x={b.x - 5} y={b.y - 5} width={b.w + 10} height={b.h + 10} rx={16} fill="none"
          stroke="#AF8E5E" strokeWidth={2.2} />}
        {active && <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={12} fill="none" stroke={ACCENT}
          strokeWidth={3} className="sf-pulse" />}
        <g className="sf-node" role="button" tabIndex={0} aria-label={`${n.label} (parallel)`}
          onClick={ev => { ev.stopPropagation(); onSelect({ kind: 'node', id: n.id }); }}
          onKeyDown={ev => { if (ev.key === 'Enter') onSelect({ kind: 'node', id: n.id }); }}>
          <title>{`${n.label}${n.code ? ` — ${n.code}` : ''}\n${n.summary || ''}`}</title>
          {shape(b, 'parallel', { ...look, fill: look.phase === 'done' && mode === 'scenario' ? '#EAF1FA' : look.fill })}
          <text x={b.x + 12} y={b.y + 16} fontSize={11.5} fontWeight={700} fill="#141414">
            {wrap(n.label, Math.floor((b.w - 80) / 6.6), 1)[0]}
          </text>
          <text x={b.x + 12} y={b.y + 30} fontSize={9.6} fill="#525252">
            {wrap(n.summary, Math.floor((b.w - 24) / 5.2), 1)[0]}
          </text>
          <g style={{ pointerEvents: 'none' }}>
            <rect x={b.x + b.w - 62} y={b.y + 6} width={54} height={14} rx={7} fill="#DCE7F1" />
            <text x={b.x + b.w - 35} y={b.y + 16} textAnchor="middle" fontSize={8.5} fontWeight={700} fill="#2C5695">
              IN PARALLEL
            </text>
          </g>
        </g>
        {(n.substeps || []).map(s => {
          const sb = layout.subs.get(`${n.id}/${s.id}`);
          if (!sb) return null;
          const lk = subLook(n.id, sb);
          const sSel = selected?.kind === 'node' && selected.id === n.id && selected.sub === s.id;
          const tb = layout.orientation === 'tb';
          const sumLines = wrap(s.summary, Math.floor((sb.w - 12) / 5), tb ? 2 : 1);
          const ms = lk.step ? num(lk.step.ms) : null;
          const showMs = mode === 'trace' && lk.step && (lk.state === 'done' || lk.state === 'running');
          return (
            <g key={s.id} className="sf-node" role="button" tabIndex={0} aria-label={`${n.label}: ${s.label}`}
              onClick={ev => { ev.stopPropagation(); onSelect({ kind: 'node', id: n.id, sub: s.id }); }}
              onKeyDown={ev => { if (ev.key === 'Enter') onSelect({ kind: 'node', id: n.id, sub: s.id }); }}>
              <title>{`${s.label}\n${s.summary || ''}`}</title>
              {sSel && <rect x={sb.x - 3} y={sb.y - 3} width={sb.w + 6} height={sb.h + 6} rx={9} fill="none"
                stroke="#AF8E5E" strokeWidth={2} />}
              <rect x={sb.x} y={sb.y} width={sb.w} height={sb.h} rx={7} fill={lk.fill} stroke={lk.stroke}
                strokeWidth={lk.width} className={`sf-shape ${lk.cls}`} />
              <text x={sb.x + 7} y={sb.y + 14} fontSize={10.3} fontWeight={700} fill="#141414">
                {wrap(s.label, Math.floor((sb.w - (showMs ? 54 : 14)) / 6), 1)[0]}
              </text>
              {showMs && (
                <text x={sb.x + sb.w - 6} y={sb.y + 14} textAnchor="end" fontSize={9} fontWeight={700}
                  fill={lk.state === 'running' ? ACCENT : statusStyle(lk.step!.status).text}>
                  {lk.state === 'running'
                    ? `${fmtMs(Math.max(0, frame.realNow - (num(lk.step!.t_start_ms) ?? 0)))}…`
                    : lk.step!.status === 'skipped' ? 'skipped' : fmtMs(ms)}
                </text>
              )}
              {sumLines.map((l, i) => (
                <text key={i} x={sb.x + 7} y={sb.y + 27 + i * 11} fontSize={9} fill="#525252">{l}</text>
              ))}
              {lk.state === 'running' && (
                <rect x={sb.x + 4} y={sb.y + sb.h - 4} height={2.5} rx={1.2} fill={ACCENT}
                  width={Math.max(2, (sb.w - 8) * Math.min(1, lk.progress))} />
              )}
            </g>
          );
        })}
        {mode === 'trace' && running && msBadge(b.x + b.w + 4, b.y - 10, 'ran', runMs, true)}
        {mode === 'trace' && look.phase === 'done' && look.st && msBadge(b.x + b.w + 4, b.y - 10, look.st.status, look.st.ms)}
      </g>
    );
  };

  const nodeBoxes = Array.from(layout.nodes.values());
  const dotVisible = frame.dot && frame.activeSeg?.kind === 'edge';

  return (
    <svg width={layout.width * scale} height={layout.height * scale}
      viewBox={`0 0 ${layout.width} ${layout.height}`} role="img"
      aria-label="Search pipeline flow" style={{ display: 'block', fontFamily: '"DM Sans", sans-serif' }}>
      <defs>
        {([['grey', EDGE_GREY], ['navy', ACCENT], ['red', '#D0443A'], ['purple', EDGE_LOOP]] as const).map(([n, c]) => (
          <marker key={n} id={mk(n)} viewBox="0 0 10 10" refX={9} refY={5} markerWidth={9} markerHeight={9}
            markerUnits="userSpaceOnUse" orient="auto">
            <path d="M0,1 L9,5 L0,9 z" fill={c} />
          </marker>
        ))}
        <radialGradient id={mk('glow')}>
          <stop offset="0%" stopColor={DOT} stopOpacity={0.55} />
          <stop offset="100%" stopColor={DOT} stopOpacity={0} />
        </radialGradient>
      </defs>

      {layout.lanes.map(l => (
        <g key={l.lane}>
          <rect x={l.box.x} y={l.box.y} width={l.box.w} height={l.box.h} rx={10}
            fill={l.index % 2 === 0 ? '#FAF8F4' : '#FFFFFF'} />
          <text x={l.labelAt.x} y={l.labelAt.y} textAnchor={layout.orientation === 'lr' ? 'middle' : 'start'}
            dominantBaseline="middle" fontSize={10} fontWeight={700} letterSpacing={1.2} fill="#9A7A4C">
            {l.label.toUpperCase()}
          </text>
        </g>
      ))}

      {edgeList.map(e => renderEdge(e.id))}

      {nodeBoxes.map(b => (isParallel(b.node) ? renderGroup(b) : renderNode(b)))}

      {/* labels over the nodes: a short edge between neighbours is narrower than its label */}
      {edgeList.map(e => renderEdgeLabel(e.id))}

      {dotVisible && (
        <g style={{ pointerEvents: 'none' }}>
          <circle cx={frame.dot!.x} cy={frame.dot!.y} r={14} fill={`url(#${mk('glow')})`} />
          <circle cx={frame.dot!.x} cy={frame.dot!.y} r={5.5} fill={DOT} stroke="#FFFFFF" strokeWidth={2} />
        </g>
      )}
    </svg>
  );
};

export default FlowGraph;

/** Legend chips for the kinds, edge styles and run statuses. */
export const FlowLegend: React.FC<{ showStatus: boolean }> = ({ showStatus }) => (
  <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-[11px] text-grayscale-1100">
    {Object.entries(KIND_STYLE).map(([k, s]) => (
      <span key={k} className="inline-flex items-center gap-1">
        <svg width="18" height="12" aria-hidden>
          {k === 'decision'
            ? <polygon points="4,1 14,1 17,6 14,11 4,11 1,6" fill={s.fill} stroke={s.stroke} strokeWidth={1.3} />
            : <rect x="1" y="1" width="16" height="10" rx="3" fill={s.fill} stroke={s.stroke} strokeWidth={1.3}
                strokeDasharray={s.dash ? '3 2' : undefined} />}
        </svg>
        {s.label}
      </span>
    ))}
    <span className="inline-flex items-center gap-1">
      <svg width="22" height="8" aria-hidden><line x1="1" y1="4" x2="21" y2="4" stroke={EDGE_GREY} strokeWidth={1.6} /></svg>
      normal
    </span>
    <span className="inline-flex items-center gap-1">
      <svg width="22" height="8" aria-hidden><line x1="1" y1="4" x2="21" y2="4" stroke={EDGE_FALLBACK} strokeWidth={1.6} strokeDasharray="5 3" /></svg>
      fallback
    </span>
    <span className="inline-flex items-center gap-1">
      <svg width="22" height="8" aria-hidden><line x1="1" y1="4" x2="21" y2="4" stroke={ACCENT} strokeWidth={2.2} strokeDasharray="6 4" /></svg>
      path taken
    </span>
    {showStatus && ['ran', 'skipped', 'cached', 'degraded', 'failed'].map(s => (
      <span key={s} className="inline-flex items-center gap-1">
        <span className="inline-block w-3 h-3 rounded-sm border"
          style={{ background: statusStyle(s).fill, borderColor: statusStyle(s).stroke }} />
        {s}
      </span>
    ))}
  </div>
);

